import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Card,
  CardContent,
  Grid,
  Divider,
} from '@mui/material';
import {
  DeviceHub,
  Schedule,
  Inventory,
  AccountTree,
} from '@mui/icons-material';

/**
 * 合成路线图展示组件（横向布局）
 * 用于显示物品的生产合成路线树状结构
 */
const ProductionRouteGraph = ({ routeData, selectedRouteIndex = 0 }) => {
  const svgRef = useRef(null);
  const scrollContainerRef = useRef(null);

  // 处理触摸事件，防止滑动返回手势
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    let touchStartX = 0;
    let touchStartY = 0;
    let scrollLeft = 0;
    let scrollTop = 0;
    let isScrolling = false;

    const handleTouchStart = (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      scrollLeft = container.scrollLeft;
      scrollTop = container.scrollTop;
      isScrolling = false;
    };

    const handleTouchMove = (e) => {
      if (!isScrolling) {
        const touchX = e.touches[0].clientX;
        const touchY = e.touches[0].clientY;
        const deltaX = touchX - touchStartX;
        const deltaY = touchY - touchStartY;
        
        // 判断是否主要是横向滑动（使用更大的阈值）
        const isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 5;
        
        if (isHorizontalSwipe) {
          isScrolling = true;
        }
      }
      
      if (isScrolling) {
        const touchX = e.touches[0].clientX;
        const deltaX = touchX - touchStartX;
        
        // 增加边缘检测的容差（5px）
        const atLeftEdge = scrollLeft <= 5;
        const atRightEdge = scrollLeft >= container.scrollWidth - container.clientWidth - 5;
        
        // 在边缘时阻止浏览器手势
        // 左边缘且向右滑动（deltaX > 0）= 返回手势
        // 右边缘且向左滑动（deltaX < 0）= 前进手势
        if ((atLeftEdge && deltaX > 10) || (atRightEdge && deltaX < -10)) {
          e.preventDefault();
          e.stopPropagation();
        }
      }
      // 纵向滑动不做处理，让它自然传递到页面
    };

    const handleTouchEnd = () => {
      isScrolling = false;
    };

    // 使用 passive: false 来允许 preventDefault
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });
    container.addEventListener('touchcancel', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [routeData, selectedRouteIndex]);

  // 调试：打印接收到的数据
  useEffect(() => {
    if (routeData) {
      console.log('ProductionRouteGraph received data:', routeData);
      if (routeData.routes && routeData.routes.length > 0) {
        console.log('First route:', routeData.routes[0]);
      }
    }
  }, [routeData]);

  if (!routeData || !routeData.routes || routeData.routes.length === 0) {
    return (
      <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
        <AccountTree sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          暂无合成路线数据
        </Typography>
      </Paper>
    );
  }

  // 格式化时间显示
  const formatTime = (seconds) => {
    if (!seconds) return '0秒';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    const parts = [];
    if (hours > 0) parts.push(`${hours}小时`);
    if (minutes > 0) parts.push(`${minutes}分钟`);
    if (secs > 0 || parts.length === 0) parts.push(`${secs}秒`);
    
    return parts.join(' ');
  };

  // 计算树的布局（改进的布局算法，确保节点不重叠）
  const calculateLayout = (node, level = 0, offsetY = 0, parentPath = '', maxDepth = 0, visitedPath = []) => {
    const nodeWidth = 168;
    const nodeHeight = 108;
    const horizontalGap = 140;  // 层级之间的横向间距
    const verticalGap = 50;     // 同级节点的纵向间距
    const minNodeSpacing = 30;  // 节点之间的最小间距
    
    // 生成唯一ID：使用路径拼接，确保树中每个位置的节点都有唯一标识
    const uniqueId = parentPath ? `${parentPath}-${node.details.good_id}` : `${node.details.good_id}`;
    
    // 更新访问路径（包含当前节点的good_id和uniqueId）
    const currentPath = [...visitedPath, { goodId: node.details.good_id, uniqueId: uniqueId }];
    
    const children = node.children || [];
    const childCount = children.length;
    
    // 如果没有子节点，节点占据一个基本高度
    if (childCount === 0) {
      return {
        node: node,
        uniqueId: uniqueId,
        level: level,
        x: 0, // 先设为0，后续会调整
        y: offsetY,
        height: nodeHeight,
        subtreeHeight: nodeHeight, // 子树高度就是节点本身高度
        children: [],
        visitedPath: currentPath
      };
    }
    
    // 递归计算所有子节点的布局
    // 关键：为每个子节点分配固定的垂直空间，确保它们不重叠
    let allocatedY = offsetY; // 当前分配的Y位置
    const childLayouts = [];
    
    for (let i = 0; i < children.length; i++) {
      const childLayout = calculateLayout(children[i], level + 1, allocatedY, uniqueId, maxDepth, currentPath);
      childLayouts.push(childLayout);
      // 下一个子节点的分配位置 = 当前位置 + 当前子树高度 + 间距
      allocatedY += childLayout.subtreeHeight + verticalGap;
    }
    
    // 移除最后一个多余的间距
    const totalAllocatedHeight = allocatedY - offsetY - verticalGap;
    
    // 父节点Y坐标居中于所有子节点分配的空间
    const nodeY = offsetY + (totalAllocatedHeight - nodeHeight) / 2;
    
    return {
      node: node,
      uniqueId: uniqueId,
      level: level,
      x: 0, // 先设为0，后续会调整
      y: nodeY,
      height: nodeHeight,
      subtreeHeight: totalAllocatedHeight, // 记录子树总高度
      children: childLayouts,
      visitedPath: currentPath
    };
  };

  // 渲染单个物料节点
  const renderMaterialNode = (nodeLayout, isTarget = false, maxDepth = 0) => {
    const node = nodeLayout.node;
    const details = node.details;
    const isBaseMaterial = details.equipment_id === -1;
    // 生成更稳定的 key：包含位置信息和节点ID
    const nodeKey = `node-${nodeLayout.uniqueId}-x${Math.round(nodeLayout.x)}-y${Math.round(nodeLayout.y)}`;
    
    // 优化的z-index计算策略：
    // 1. 右侧的节点（X大，目标方向）基础z-index更高：每100px的X增加100 z-index
    // 2. 下方的节点（Y大）z-index略高：每像素增加0.1，这样下方节点在重叠时显示在前面
    // 3. 确保即使有轻微重叠，下方节点也能完整显示
    const xFactor = Math.floor(nodeLayout.x / 10); // X每10px增加1 z-index
    const yFactor = Math.floor(nodeLayout.y * 0.1); // Y每10px增加1 z-index
    const zIndex = xFactor + yFactor;
    
    return (
      <Box
        key={nodeKey}
        id={nodeKey}
        sx={{
          position: 'absolute',
          left: nodeLayout.x,
          top: nodeLayout.y,
          width: 168,
          zIndex: zIndex,
          // 允许触摸滚动穿透
          touchAction: 'pan-x pan-y'
        }}
      >
        <Card 
          sx={{ 
            border: isTarget ? 3 : 2,
            borderColor: isTarget ? 'primary.main' : isBaseMaterial ? 'success.main' : 'secondary.main',
            bgcolor: isTarget ? 'primary.50' : isBaseMaterial ? 'success.50' : 'background.paper',
            boxShadow: 3,
            // 防止长按选择文本阻止滚动
            userSelect: 'none',
            WebkitUserSelect: 'none',
            // 关键：允许触摸滚动穿透节点
            touchAction: 'pan-x pan-y'
          }}
        >
          <CardContent sx={{ 
            p: 1, 
            '&:last-child': { pb: 1 },
            // 确保内容区域也允许滚动穿透
            touchAction: 'pan-x pan-y'
          }}>
            {/* 物料图片和名称 */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.75, 
              mb: 0.75,
              touchAction: 'pan-x pan-y'
            }}>
              {details.good_image_name ? (
                <Box
                  component="img"
                  src={`http://139.224.28.156:5000/images/${details.good_image_name}`}
                  alt={details.good_name || `物料 ${details.good_id}`}
                  sx={{ 
                    width: 50, 
                    height: 50, 
                    objectFit: 'contain',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    p: 0.5,
                    bgcolor: 'white',
                    // 防止图片拖拽干扰滚动
                    pointerEvents: 'none',
                    userDrag: 'none',
                    WebkitUserDrag: 'none'
                  }}
                  onError={(e) => { 
                    e.target.style.display = 'none'; 
                  }}
                  draggable={false}
                />
              ) : (
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    bgcolor: 'grey.200',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none'
                  }}
                >
                  <Inventory color="disabled" sx={{ fontSize: 14, pointerEvents: 'none' }} />
                </Box>
              )}
              <Box sx={{ flex: 1, minWidth: 0, pointerEvents: 'none' }}>
                <Typography variant="caption" fontWeight="bold" noWrap sx={{ fontSize: '0.8rem' }}>
                  {details.good_name || `物料 ${details.good_id}`}
                </Typography>
                {isTarget && (
                  <Chip label="目标" size="small" color="primary" sx={{ height: 19, fontSize: '0.7rem', pointerEvents: 'none' }} />
                )}
                {isBaseMaterial && (
                  <Chip label="基础" size="small" color="success" sx={{ height: 19, fontSize: '0.7rem', pointerEvents: 'none' }} />
                )}
              </Box>
            </Box>

            <Divider sx={{ my: 0.5 }} />

            {/* 数量信息 */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, touchAction: 'pan-x pan-y' }}>
              {/* 左侧：数量 */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pointerEvents: 'none' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  数量
                </Typography>
                <Typography variant="body2" fontWeight="bold" color="primary" sx={{ fontSize: '0.85rem' }}>
                  {(details.count * 1).toFixed(1)}
                </Typography>
              </Box>
              
              {/* 右侧：传送带 */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pointerEvents: 'none' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  传送带
                </Typography>
                <Typography variant="body2" fontWeight="bold" color="secondary" sx={{ fontSize: '0.85rem' }}>
                  {(details.count / 30).toFixed(1)}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  };

  // 渲染箭头和连接线（横向布局）
  const renderConnections = (layout, parentLayout = null, allLayouts = []) => {
    const lines = [];
    const node = layout.node;
    const children = layout.children || [];
    
    // 调试日志
    console.log(`Rendering connections for node ${node.details.good_id}, children count: ${children.length}`, {
      nodePos: { x: layout.x, y: layout.y },
      children: children.map(c => ({ id: c.node.details.good_id, x: c.x, y: c.y }))
    });
    
    // 检测循环引用：如果子节点的good_id在祖先路径中出现，绘制从子节点返回到父节点的循环连线
    children.forEach((childLayout) => {
      const childGoodId = childLayout.node.details.good_id;
      const visitedPath = layout.visitedPath || [];
      
      // 检查是否形成循环（子节点的good_id在访问路径中出现过）
      const circularAncestor = visitedPath.find(ancestor => ancestor.goodId === childGoodId);
      
      if (circularAncestor) {
        // 循环引用：绘制从子节点（循环节点）返回到当前节点（父节点）的连线
        // 这表示：父节点需要子节点，而子节点又需要父节点，形成双向依赖
        // 子节点到父节点的循环连线在节点3/5高度处（64.8）
        // 父节点到子节点的普通连线在节点2/5高度处（43.2）
        const childCircularY = childLayout.y + 64.8; // 子节点循环连线：3/5高度
        const parentCircularY = layout.y + 64.8;     // 父节点循环连线：3/5高度
        const childRight = childLayout.x + 168;
        const parentLeft = layout.x;
        
        // 使用虚线和橙色表示循环
        const curveOffset = 30; // 曲线偏移量
        const controlPointX1 = childRight + curveOffset;
        const controlPointX2 = parentLeft - curveOffset;
        
        // 使用贝塞尔曲线绘制循环连线：从子节点右侧到父节点左侧
        const path = `M ${childRight} ${childCircularY} C ${controlPointX1} ${childCircularY}, ${controlPointX2} ${parentCircularY}, ${parentLeft} ${parentCircularY}`;
        
        lines.push(
          <path
            key={`circular-${childLayout.uniqueId}-to-${layout.uniqueId}`}
            d={path}
            stroke="#ff9800"
            strokeWidth="2"
            fill="none"
            opacity="0.9"
          />
        );
        
        // 添加子节点右侧的黄色箭头（从右指向左）
        lines.push(
          <polygon
            key={`circular-arrow-child-${childLayout.uniqueId}`}
            points={`${childRight},${childCircularY} ${childRight + 12},${childCircularY - 6} ${childRight + 12},${childCircularY + 6}`}
            fill="#ff9800"
            opacity="0.9"
          />
        );
        
        // 添加循环标签（显示在连线下方，包含设备名称和数量）
        const labelX = (childRight + parentLeft) / 2 - 9;
        const labelY = Math.min(childCircularY, parentCircularY) + 20;
        const childNode = childLayout.node;
        lines.push(
          <g key={`circular-label-${childLayout.uniqueId}-to-${layout.uniqueId}`}>
            <text
              x={labelX}
              y={labelY}
              textAnchor="middle"
              fill="#ff9800"
              fontSize="15"
              fontWeight="bold"
            >
              {childNode.details.equipment_name && childNode.details.equipment_id !== -1 
                ? `${(childNode.details.count/(60/childNode.details.time)).toFixed(1)}${childNode.details.equipment_name}`
                : ''
              }
            </text>
          </g>
        );
      }
    });
    
    // 先检测是否有循环引用
    const visitedPath = layout.visitedPath || [];
    const hasCircular = children.some(childLayout => {
      const childGoodId = childLayout.node.details.good_id;
      return visitedPath.find(ancestor => ancestor.goodId === childGoodId);
    });
    
    if (children.length > 0) {
      const nodeLeft = layout.x; // 父节点左边
      const nodeHeight = 108;
      // 如果有循环引用，父节点到子节点的连线在2/5高度处，否则在中心
      // 保持蓝色连线水平：父节点和子节点都在同一高度
      const nodeConnectionY = hasCircular ? (layout.y + 43.2) : (layout.y + nodeHeight / 2);
      
      // 如果有多个子节点，绘制汇合的箭头
      if (children.length > 1) {
        const childrenRight = children[0].x + 168; // 子节点右边
        const midX = childrenRight + (nodeLeft - childrenRight) * 0.25; // 汇合点X（更靠近子节点）
        
        // 从每个子节点到汇合点的线
        children.forEach((childLayout, index) => {
          // 所有子节点的连线位置：如果父节点有循环引用，统一使用2/5高度（与父节点同高度，保持水平）
          const childConnectionY = hasCircular ? (childLayout.y + 43.2) : (childLayout.y + nodeHeight / 2);
          const childRight = childLayout.x + 168; // 子节点右边
          
          // 横向线：从子节点右侧到汇合点
          lines.push(
            <line
              key={`child-to-mid-h-${childLayout.uniqueId}`}
              x1={childRight}
              y1={childConnectionY}
              x2={midX}
              y2={childConnectionY}
              stroke="#1976d2"
              strokeWidth="1.5"
            />
          );
          
          // 纵向线：从子节点的Y位置到父节点的Y位置
          lines.push(
            <line
              key={`child-to-mid-v-${childLayout.uniqueId}`}
              x1={midX}
              y1={childConnectionY}
              x2={midX}
              y2={nodeConnectionY}
              stroke="#1976d2"
              strokeWidth="1.5"
            />
          );
        });
        
        // 从汇合点到父节点的横向主线
        lines.push(
          <line
            key={`mid-to-parent-${layout.uniqueId}`}
            x1={midX}
            y1={nodeConnectionY}
            x2={nodeLeft - 14}
            y2={nodeConnectionY}
            stroke="#1976d2"
            strokeWidth="2"
          />
        );
        
        // 箭头（在父节点左侧外，指向右边）
        lines.push(
          <polygon
            key={`arrow-${layout.uniqueId}`}
            points={`${nodeLeft - 2},${nodeConnectionY} ${nodeLeft - 14},${nodeConnectionY - 6} ${nodeLeft - 14},${nodeConnectionY + 6}`}
            fill="#1976d2"
          />
        );
        
        // 在汇合后箭头的中间显示设备名称和生产时间（标签在连线上方）
        if (node.details.equipment_name && node.details.equipment_id !== -1) {
          const labelX = (midX + nodeLeft) / 2; // 标签位于汇合点和父节点左边的中间
          lines.push(
            <g key={`label-${layout.uniqueId}`}>
              <text
                x={labelX}
                y={nodeConnectionY - 8}
                textAnchor="middle"
                fill="#1976d2"
                fontSize="15"
                fontWeight="bold"
              >
                {(node.details.count/(60/node.details.time)).toFixed(1)}
                {node.details.equipment_name}
              </text>
            </g>
          );
        }
      } else {
        // 只有一个子节点
        const childLayout = children[0];
        // 所有子节点的连线位置：如果父节点有循环引用，统一使用2/5高度（与父节点同高度，保持水平）
        const childConnectionY = hasCircular ? (childLayout.y + 43.2) : (childLayout.y + nodeHeight / 2);
        const childRight = childLayout.x + 168; // 子节点右边
        
        // 如果父子节点Y坐标接近（差距小于30像素），直接画横线
        if (Math.abs(childConnectionY - nodeConnectionY) < 30) {
          lines.push(
            <line
              key={`single-child-straight-${layout.uniqueId}`}
              x1={childRight}
              y1={childConnectionY}
              x2={nodeLeft - 14}
              y2={nodeConnectionY}
              stroke="#1976d2"
              strokeWidth="2"
            />
          );
          
          // 箭头（在父节点左侧外，指向右边）
          lines.push(
            <polygon
              key={`arrow-straight-${layout.uniqueId}`}
              points={`${nodeLeft - 2},${nodeConnectionY} ${nodeLeft - 14},${nodeConnectionY - 6} ${nodeLeft - 14},${nodeConnectionY + 6}`}
              fill="#1976d2"
            />
          );
        } else {
          // Y坐标不同，使用Z字形连线（从子节点右侧直接到父节点左侧，中间带垂直段）
          const midX = (childRight + nodeLeft) / 2;
          
          // 第一段：子节点右侧到中点的横线
          lines.push(
            <line
              key={`single-child-h1-${layout.uniqueId}`}
              x1={childRight}
              y1={childConnectionY}
              x2={midX}
              y2={childConnectionY}
              stroke="#1976d2"
              strokeWidth="2"
            />
          );
          
          // 第二段：中点的纵向连接线
          lines.push(
            <line
              key={`single-child-v-${layout.uniqueId}`}
              x1={midX}
              y1={childConnectionY}
              x2={midX}
              y2={nodeConnectionY}
              stroke="#1976d2"
              strokeWidth="2"
            />
          );
          
          // 第三段：中点到父节点左侧的横线
          lines.push(
            <line
              key={`single-child-h2-${layout.uniqueId}`}
              x1={midX}
              y1={nodeConnectionY}
              x2={nodeLeft - 14}
              y2={nodeConnectionY}
              stroke="#1976d2"
              strokeWidth="2"
            />
          );
          
          // 箭头（在父节点左侧外，指向右边）
          lines.push(
            <polygon
              key={`arrow-fold-${layout.uniqueId}`}
              points={`${nodeLeft - 2},${nodeConnectionY} ${nodeLeft - 14},${nodeConnectionY - 6} ${nodeLeft - 14},${nodeConnectionY + 6}`}
              fill="#1976d2"
            />
          );
        }
        
        // 设备标签显示在连线中间（标签在连线上方）
        if (node.details.equipment_name && node.details.equipment_id !== -1) {
          const labelX = (childRight + nodeLeft) / 2;
          lines.push(
            <g key={`label-single-${layout.uniqueId}`}>
              <text
                x={labelX}
                y={nodeConnectionY - 8}
                textAnchor="middle"
                fill="#1976d2"
                fontSize="15"
                fontWeight="bold"
              >
                {(node.details.count/(60/node.details.time)).toFixed(1)}
                {node.details.equipment_name}
              </text>
            </g>
          );
        }
      }
      
      // 递归渲染子节点的连接
      children.forEach(childLayout => {
        lines.push(...renderConnections(childLayout, layout, allLayouts));
      });
    }
    
    return lines;
  };

  // 渲染所有节点
  const renderAllNodes = (layout, parentKey = '', maxDepth = 0) => {
    const nodes = [];
    const isTarget = layout.node.details.good_id === routeData.good_id && layout.level === 0;
    
    nodes.push(renderMaterialNode(layout, isTarget, maxDepth));
    
    const children = layout.children || [];
    children.forEach((childLayout, childIndex) => {
      nodes.push(...renderAllNodes(childLayout, `${parentKey}-child${childIndex}`, maxDepth));
    });
    
    return nodes;
  };

  // 渲染路线
  const renderRoute = (route, routeIndex) => {
    if (!route || !route.root_nodes || route.root_nodes.length === 0) {
      return (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }} key={`route-empty-${routeIndex}`}>
          <Typography color="text.secondary">该路线没有数据</Typography>
        </Paper>
      );
    }
    
    // 验证数据完整性
    const validRootNodes = route.root_nodes.filter(node => 
      node && node.details && typeof node.details.good_id !== 'undefined'
    );
    
    if (validRootNodes.length === 0) {
      return (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }} key={`route-invalid-${routeIndex}`}>
          <Typography color="text.secondary">该路线数据不完整</Typography>
        </Paper>
      );
    }
    
    // 计算所有根节点的布局（横向布局，根节点纵向排列）
    const layouts = [];
    let currentY = 50;
    
    // 先计算最大深度
    let maxDepth = 0;
    validRootNodes.forEach(rootNode => {
      try {
        const tempLayout = calculateLayout(rootNode, 0, 0);
        const depth = getMaxDepth(tempLayout);
        if (depth > maxDepth) maxDepth = depth;
      } catch (error) {
        console.error('Error calculating layout depth:', error, rootNode);
      }
    });
    
    // 使用最大深度调整x坐标，让子节点在左边，父节点在右边
    const adjustXCoordinates = (layout, maxDepth) => {
      const nodeWidth = 168;
      const horizontalGap = 140; // 保持与calculateLayout中一致
      layout.x = (maxDepth - layout.level - 1) * (nodeWidth + horizontalGap) + 50;
      if (layout.children && Array.isArray(layout.children)) {
        layout.children.forEach(child => adjustXCoordinates(child, maxDepth));
      }
    };
    
    validRootNodes.forEach((rootNode, index) => {
      try {
        const layout = calculateLayout(rootNode, 0, currentY);
        adjustXCoordinates(layout, maxDepth);
        layouts.push(layout);
        currentY = layout.y + layout.height + 50;
      } catch (error) {
        console.error('Error calculating layout for root node:', error, rootNode);
      }
    });
    
    // 计算整体宽度和高度（横向布局）
    const nodeWidth = 168;
    const horizontalGap = 140;
    const totalWidth = maxDepth * (nodeWidth + horizontalGap) + 200; // 每层宽度 = nodeWidth(168) + horizontalGap(140)
    // 计算总高度：找到最后一个根节点的底部位置（使用subtreeHeight）
    const lastLayout = layouts[layouts.length - 1];
    const totalHeight = lastLayout ? lastLayout.y + lastLayout.subtreeHeight + 50 : 500;

    console.log('Layout calculation:', {
      rootNodesCount: route.root_nodes.length,
      maxDepth,
      totalWidth,
      totalHeight,
      layouts: layouts.map(l => ({ x: l.x, y: l.y, height: l.height }))
    });

    return (
      <Paper elevation={2} sx={{ p: 3, mb: 3 }} key={routeIndex}>
        {/* 路线标题 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
          <AccountTree color="primary" />
          <Typography variant="h6">
            路线 {routeIndex + 1}
          </Typography>
        </Box>
        
        {/* 路线内容 - 使用相对定位的容器 */}
        <Box 
          ref={scrollContainerRef}
          key={`scroll-container-${routeIndex}`}
          sx={{ 
            position: 'relative', 
            width: '100%', 
            maxHeight: 800, 
            overflow: 'auto', 
            bgcolor: '#f5f5f5', 
            borderRadius: 2, 
            p: 2,
            // 改进移动端滚动体验
            WebkitOverflowScrolling: 'touch',
            // 确保触摸事件可以传递
            touchAction: 'pan-x pan-y',
            // 强制启用滚动
            overflowX: 'auto',
            overflowY: 'auto',
            // 关键：只阻止横向的过度滚动，允许纵向滚动传递到页面
            overscrollBehavior: 'contain none',  // x轴contain，y轴none
            overscrollBehaviorX: 'contain',
            overscrollBehaviorY: 'auto',  // 允许纵向滚动传递
            // 确保内容可滚动
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: '4px'
            }
          }}
        >
          {/* 内容容器 - 包含SVG和节点，确保它们一起滚动 */}
          <Box 
            key={`content-container-${routeIndex}`}
            sx={{ position: 'relative', width: totalWidth, height: totalHeight, minHeight: totalHeight }}
          >
            {/* SVG 层 - 绘制连接线，使用完整的viewBox */}
            <svg
              ref={svgRef}
              viewBox={`0 0 ${totalWidth} ${totalHeight}`}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                // SVG本身不拦截事件，让事件传递到下方的滚动容器
                pointerEvents: 'none',
                zIndex: 1,
                overflow: 'visible'
              }}
              preserveAspectRatio="xMinYMin meet"
            >
              {layouts.map((layout, index) => (
                <g key={`svg-connections-route${routeIndex}-layout${index}-${layout.uniqueId || index}`}>
                  {renderConnections(layout, null, layouts)}
                </g>
              ))}
            </svg>
            
            {/* 节点层 - 直接渲染节点，不使用包装容器避免事件拦截 */}
            {layouts.map((layout, index) => 
              renderAllNodes(layout, '', maxDepth)
            ).flat()}
          </Box>
        </Box>
      </Paper>
    );
  };

  // 获取最大深度（用于计算容器宽度）
  const getMaxDepth = (layout, depth = 1) => {
    if (!layout.children || layout.children.length === 0) {
      return depth;
    }
    return Math.max(...layout.children.map(child => getMaxDepth(child, depth + 1)));
  };

  return (
    <Box>
      {/* 路线摘要 */}
      {/* <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {routeData.good_image_name && (
                <Box
                  component="img"
                  src={`http://139.224.28.156:5000/images/${routeData.good_image_name}`}
                  alt={routeData.good_name}
                  sx={{ width: 48, height: 48, objectFit: 'contain' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <Inventory color="primary" />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  目标产物
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {routeData.good_name || `物料 ID: ${routeData.good_id}`}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccountTree color="secondary" />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  路线数量
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {routeData.route_count || 0} 条路线
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DeviceHub color="success" />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  生产数量
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {routeData.quantity}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper> */}

      {/* 当前选中的路线 */}
      <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
        <AccountTree sx={{ verticalAlign: 'middle', mr: 1 }} />
        路线 {selectedRouteIndex + 1} - 详情
      </Typography>

      {routeData.routes[selectedRouteIndex] && renderRoute(routeData.routes[selectedRouteIndex], selectedRouteIndex)}

      {/* 缓存统计
      {routeData.cache_stats && (
        <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            缓存统计
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="text.secondary">缓存命中</Typography>
              <Typography variant="h6">{routeData.cache_stats.hits || 0}</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="text.secondary">缓存未命中</Typography>
              <Typography variant="h6">{routeData.cache_stats.misses || 0}</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="text.secondary">缓存大小</Typography>
              <Typography variant="h6">{routeData.cache_stats.size || 0}</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="text.secondary">命中率</Typography>
              <Typography variant="h6">
                {routeData.cache_stats.hits && routeData.cache_stats.misses
                  ? `${((routeData.cache_stats.hits / (routeData.cache_stats.hits + routeData.cache_stats.misses)) * 100).toFixed(1)}%`
                  : 'N/A'}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )} */}
    </Box>
  );
};

export default ProductionRouteGraph;
